import { useEffect, useMemo, useState } from "react";
import { v4 as uid } from "uuid";
import Cookies from "js-cookie";

const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

const persistTodos = (items) => {
  Cookies.set("todos", JSON.stringify(items));
  window.chrome?.storage?.local?.set({ todos: items });
};

export default function Todo({
  setTodos,
  todos = [],
  opacity,
  onChange,
  readOnly = false,
  inline = false,
  disablePersistence = false,
  title = "Tasks",
  helperText,
}) {
  const [newTodo, setNewTodo] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!todos.length || disablePersistence) return;
    persistTodos(todos);
  }, [disablePersistence, todos]);

  const sortedTodos = useMemo(
    () =>
      [...todos].sort((a, b) => {
        if (a.isComplete === b.isComplete) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return a.isComplete ? 1 : -1;
      }),
    [todos]
  );

  if (isMobile) return null;

  const updateTodos = (items) => {
    setTodos(items);
    if (!disablePersistence) {
      persistTodos(items);
    }
    onChange?.(items);
  };

  const addTodo = (e) => {
    e.preventDefault();
    if (!newTodo.trim() || readOnly) return;

    const updatedTodos = [
      ...todos,
      {
        title: newTodo.trim(),
        isComplete: false,
        index: uid(),
        createdAt: new Date().toISOString(),
      },
    ];

    updateTodos(updatedTodos);
    setNewTodo("");
  };

  const removeTodo = (index) => {
    if (readOnly) return;
    const updatedTodos = todos.filter((todo) => todo.index !== index);
    updateTodos(updatedTodos);
  };

  const toggleComplete = (index) => {
    if (readOnly) return;
    const updatedTodos = todos.map((todo) =>
      todo.index === index ? { ...todo, isComplete: !todo.isComplete } : todo
    );
    updateTodos(updatedTodos);
  };

  const taskList = (
    <div className="flex flex-col gap-2">
      {sortedTodos.map((todo) => (
        <div
          key={todo.index}
          className={`flex items-center justify-between rounded-xl border border-white/5 px-4 py-3 text-sm transition ${
            todo.isComplete ? "bg-white/5 opacity-70" : "bg-white/10"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => toggleComplete(todo.index)}
              disabled={readOnly}
              className={`flex h-5 w-5 items-center justify-center rounded-full border transition ${
                todo.isComplete
                  ? "border-emerald-400 bg-emerald-400/20 text-emerald-200"
                  : "border-white/40 text-white/50 hover:border-white/70"
              } ${readOnly ? "cursor-default opacity-60" : ""}`}
              aria-label={
                todo.isComplete ? "Mark incomplete" : "Mark complete"
              }
            >
              {todo.isComplete ? "✓" : ""}
            </button>
            <span
              className={`text-white ${
                todo.isComplete ? "line-through text-white/60" : ""
              }`}
            >
              {todo.title}
            </span>
          </div>
          {!readOnly && (
            <button
              onClick={() => removeTodo(todo.index)}
              className="text-white/50 transition hover:text-white"
              aria-label="Remove task"
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const content = (
    <div
      className={`w-full ${
        inline
          ? "rounded-2xl border border-white/10 bg-white/5 p-5"
          : `max-w-xl rounded-2xl border border-white/10 p-6 shadow-2xl ${
              opacity ? "bg-white/10" : "bg-neutral-900"
            }`
      }`}
    >
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/60">
            focus stack
          </p>
          <h3 className="text-2xl font-bold text-white">{title}</h3>
          {helperText && (
            <p className="text-sm text-white/60">{helperText}</p>
          )}
        </div>
        {!inline && (
          <button
            onClick={() => setShowModal(false)}
            className="rounded-full bg-white/10 px-3 py-1 text-sm text-white hover:bg-white/20"
            aria-label="Close tasks"
          >
            Close
          </button>
        )}
      </div>

      {!readOnly && (
        <form onSubmit={addTodo} className="mb-5 space-y-3">
          <label className="block text-sm text-white/60">
            What will you get done this session?
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              placeholder="Draft blog outline, finish chapter 3..."
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-white/40 focus:outline-none"
            />
            <button
              type="submit"
              className="rounded-xl bg-white px-4 py-3 text-sm font-semibold text-black transition hover:bg-white/90"
            >
              Add
            </button>
          </div>
        </form>
      )}

      {taskList}

      {todos.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-white/20 bg-white/5 p-6 text-center text-sm text-white/60">
          No tasks yet. {readOnly ? "Waiting for updates." : "Add one to get started!"}
        </div>
      )}
    </div>
  );

  if (inline) {
    return content;
  }

  if (!showModal) {
    return (
      <button
        onClick={() => setShowModal(true)}
        className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
          opacity
            ? "bg-white/10 text-white hover:bg-white/20"
            : "bg-white text-black hover:bg-white/90"
        }`}
      >
        {todos.length > 0 ? `Tasks (${todos.length})` : "Add tasks"}
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setShowModal(false);
      }}
    >
      {content}
    </div>
  );
}
